/**
 * Access Level ของ intent ใน Advanced Router / Dynamic Router — ฝั่ง frontend.
 *
 * "ขั้นบันได" (ladder) = รายการ code เรียงจากต่ำไปสูง (index = rank) · ตั้งได้จากหน้า
 * LH Bank Configuration › Access Level Configuration (`/AccessLevelConfig`, ตาราง `cus.AccessLevel`, #914)
 * จึงมีระดับย่อยระหว่าง T1–T3 ได้ (เช่น T2.1 / T2.2 / T2.3) · T0 ล่างสุดและ T3 บนสุดเสมอ (server ล็อกไว้)
 *   ผู้ใช้ระดับสูงกว่าเข้า intent ระดับต่ำกว่าได้เสมอ (rank ผู้ใช้ >= rank intent) — ตัดสินที่ server
 *   ฝั่ง backend อยู่ที่ `BAL/BAL/FlowEngine/AccessLevelPolicy.cs` + `AccessLevelLadder.cs`
 * เมื่อสิทธิ์ไม่ผ่าน router ตอบ marker `:ACCESS_LEVEL_REQUIRED:` (ไม่บอกระดับ — แค่ว่าต้องยืนยันตัวตนเพิ่ม).
 *
 * ── ค่าในตัว vs ladder ปัจจุบัน
 * `ACCESS_LEVELS` / `ACCESS_LEVEL_OPTIONS` = **ค่าในตัว T0–T3 (fallback)** ไม่เปลี่ยนตาม server ·
 * ladder ที่ใช้จริงอ่านผ่าน `getAccessLevels()` / `getAccessLevelOptions()` / `accessLevelRank()` ซึ่งเริ่มจาก
 * ค่าในตัว แล้วเปลี่ยนเมื่อ `setAccessLevelLadder()` ถูกเรียก (ผู้เรียกคือ `utils/accessLevelStore.js` หลังโหลด
 * `GET api/v1/AccessLevel` สำเร็จ) · โหลดไม่ได้/ยังไม่รัน SQL ⇒ ใช้ค่าในตัวต่อไป = พฤติกรรมเดิมทุกประการ
 *
 * 🔴 ไฟล์นี้ต้องไม่พึ่งโมดูลอื่น (ไม่มี alias `@/`, ไม่อ่าน env ของ vite, ไม่มี vue) — `node --test` รันตรง ๆ
 *    ส่วนเรียก API อยู่ `services/accessLevelApi.js` และ state แบบ reactive อยู่ `utils/accessLevelStore.js`
 */

/**
 * ขั้นบันไดในตัว (ตรงกับ seed ของ `docs/sql/20261021/AccessLevel.sql` และ `AccessLevelLadder.Default`)
 * @type {ReadonlyArray<Readonly<{code: string, nameTh: string, nameEn: string}>>}
 */
const BUILT_IN_LADDER = Object.freeze([
  Object.freeze({ code: 'T0', nameTh: 'ทุกคน ไม่ต้องยืนยันตัวตน', nameEn: 'Everyone — no identity check' }),
  Object.freeze({ code: 'T1', nameTh: 'ระบุตัวตนเบื้องต้น (เลขบัตร / เบอร์มือถือ)', nameEn: 'Basic identification (ID card / mobile number)' }),
  Object.freeze({ code: 'T2', nameTh: 'ยืนยันตัวตนแล้ว (บนแอปต้อง PIN / สแกน)', nameEn: 'Identity verified (PIN / scan on the app)' }),
  Object.freeze({ code: 'T3', nameTh: 'ยืนยันด้วย PIN / สแกนใบหน้า', nameEn: 'Verified with PIN / face scan' }),
]);

/** ข้อความต่อท้ายแถว dropdown ของค่าที่ไม่อยู่ใน ladder (เช่น ระดับที่ถูกลบไปแล้วแต่ intent ยังค้างค่าไว้) */
const UNKNOWN_OPTION_SUFFIX = '(ไม่รู้จัก — ระบบถือเป็นระดับสูงสุด)';

/** `"T2.1 — ชื่อไทย"` · ไม่มีชื่อไทย ⇒ ใช้ชื่ออังกฤษ · ไม่มีชื่อเลย ⇒ code อย่างเดียว */
function optionTitle(level) {
  const name = level.nameTh || level.nameEn;
  return name ? `${level.code} — ${name}`.trim() : level.code;
}

/** ladder → ตัวเลือก dropdown แบบ frozen (v-select: item-title="title" item-value="value") */
function toOptions(ladder) {
  return Object.freeze(ladder.map((level) => Object.freeze({ title: optionTitle(level), value: level.code })));
}

function cleanText(value) {
  return value == null ? '' : String(value).trim();
}

/** แถวจาก server (`{code,nameTh,nameEn}`) หรือ string code → `{code (trim+upper), nameTh, nameEn}` · ไม่มี code ⇒ null */
function toLadderEntry(entry) {
  if (typeof entry === 'string') {
    const code = entry.trim().toUpperCase();
    return code ? { code, nameTh: '', nameEn: '' } : null;
  }
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const code = cleanText(entry.code ?? entry.Code).toUpperCase();
  if (!code) {
    return null;
  }
  return {
    code,
    nameTh: cleanText(entry.nameTh ?? entry.NameTh),
    nameEn: cleanText(entry.nameEn ?? entry.NameEn),
  };
}

/** ระดับในตัว เรียงจากต่ำไปสูง (index = rank) — **ค่าในตัว/fallback** ไม่เปลี่ยนตาม server */
export const ACCESS_LEVELS = Object.freeze(BUILT_IN_LADDER.map((level) => level.code));

/** ตัวเลือก dropdown ของระดับในตัว (v-select: item-title="title" item-value="value") — **ค่าในตัว/fallback** */
export const ACCESS_LEVEL_OPTIONS = toOptions(BUILT_IN_LADDER);

/** ระดับผู้ใช้จำลองเริ่มต้นของ Flow Debug / Chat Playground (สูงสุด — flow เดิมทำงานเหมือนเดิม; T3 ถูกล็อกบนสุด) */
export const DEFAULT_USER_ACCESS_LEVEL = 'T3';

const ACCESS_LEVEL_MARKER = ':ACCESS_LEVEL_REQUIRED:';
const ACCESS_LEVEL_MARKER_PATTERN_GLOBAL = /:ACCESS_LEVEL_REQUIRED:/g;

// ── ladder ปัจจุบัน (module state — เริ่มจากค่าในตัว) ─────────────────────────────
let currentLadder = BUILT_IN_LADDER;
let currentCodes = ACCESS_LEVELS;
let currentOptions = ACCESS_LEVEL_OPTIONS;

/**
 * ตั้ง ladder ปัจจุบันจากรายการที่ server ส่งมา (เรียงตาม rank แล้ว).
 * รับ `[{ code, nameTh, nameEn }]` (หรือ string code) · code ถูก trim + ตัวพิมพ์ใหญ่
 * ไม่ใช่ array / ว่าง / มีแถวที่ไม่มี code / code ซ้ำ (ไม่สนตัวพิมพ์) ⇒ ไม่เปลี่ยนอะไร คืน false
 * (ตำแหน่ง T0/T3 ไม่ตรวจที่นี่ — server เป็นคนล็อก)
 * @param {unknown} levels
 * @returns {boolean} true เมื่อเปลี่ยน ladder แล้ว
 */
export function setAccessLevelLadder(levels) {
  if (!Array.isArray(levels) || levels.length === 0) {
    return false;
  }
  const seen = new Set();
  const next = [];
  for (const entry of levels) {
    const level = toLadderEntry(entry);
    if (!level || seen.has(level.code)) {
      return false;
    }
    seen.add(level.code);
    next.push(Object.freeze(level));
  }
  currentLadder = Object.freeze(next);
  currentCodes = Object.freeze(next.map((level) => level.code));
  currentOptions = toOptions(currentLadder);
  return true;
}

/** กลับไปใช้ ladder ในตัว T0–T3 */
export function resetAccessLevelLadder() {
  currentLadder = BUILT_IN_LADDER;
  currentCodes = ACCESS_LEVELS;
  currentOptions = ACCESS_LEVEL_OPTIONS;
}

/**
 * ladder ปัจจุบันแบบเต็ม (frozen) `[{ code, nameTh, nameEn }]` เรียงจากต่ำไปสูง
 * @returns {ReadonlyArray<Readonly<{code: string, nameTh: string, nameEn: string}>>}
 */
export function getAccessLevelLadder() {
  return currentLadder;
}

/**
 * code ของ ladder ปัจจุบัน เรียงจากต่ำไปสูง (frozen)
 * @returns {ReadonlyArray<string>}
 */
export function getAccessLevels() {
  return currentCodes;
}

/**
 * ตัวเลือก dropdown ของ ladder ปัจจุบัน `[{ title: "T2.1 — ชื่อ", value: "T2.1" }]` (frozen)
 * @returns {ReadonlyArray<Readonly<{title: string, value: string}>>}
 */
export function getAccessLevelOptions() {
  return currentOptions;
}

/**
 * rank ของระดับใน ladder ปัจจุบัน (trim + ไม่สนตัวพิมพ์ เหมือน `AccessLevelPolicy.Normalize`);
 * null / ว่าง / ค่าที่ไม่รู้จัก → -1
 * @param {unknown} level
 * @returns {number}
 */
export function accessLevelRank(level) {
  if (level == null) {
    return -1;
  }
  return currentCodes.indexOf(String(level).trim().toUpperCase());
}

/**
 * true เมื่อระดับอยู่ใน ladder ปัจจุบัน
 * @param {unknown} level
 * @returns {boolean}
 */
export function isKnownAccessLevel(level) {
  return accessLevelRank(level) >= 0;
}

/**
 * true เมื่อระดับสูงกว่าขั้นล่างสุด (T0) ของ ladder ปัจจุบัน · ไม่รู้จัก ⇒ false
 * @param {unknown} level
 * @returns {boolean}
 */
export function isRestrictedAccessLevel(level) {
  return accessLevelRank(level) > 0;
}

/**
 * สถานะ badge บน canvas ของ intent:
 * `'none'` = ไม่ตั้ง / ว่าง / T0 · `'restricted'` = ระดับที่รู้จักและสูงกว่า T0 ·
 * `'unknown'` = มีค่าแต่ไม่อยู่ใน ladder (เช่น ระดับถูกลบ — server ถือเป็นระดับสูงสุด / fail-closed)
 * @param {unknown} level
 * @returns {'none' | 'restricted' | 'unknown'}
 */
export function accessLevelBadgeState(level) {
  if (cleanText(level) === '') {
    return 'none';
  }
  const rank = accessLevelRank(level);
  if (rank < 0) {
    return 'unknown';
  }
  return rank > 0 ? 'restricted' : 'none';
}

/**
 * ตัวเลือก dropdown ของ ladder ปัจจุบัน + แถวของค่าที่ intent ค้างไว้ถ้าไม่รู้จัก
 * (ให้ inspector ยังเห็นค่านั้นแทนที่จะหายเงียบ):
 * `raw` ว่าง หรือรู้จัก ⇒ คืน `getAccessLevelOptions()` ตัวเดิม (reference เดียวกัน) ·
 * ไม่รู้จัก ⇒ array ใหม่ = ตัวเลือกทั้งหมด + `{ title: "<raw> (ไม่รู้จัก — ระบบถือเป็นระดับสูงสุด)", value: raw, unknown: true }`
 * @param {unknown} raw ค่า accessLevel ที่ตั้งบน intent
 * @returns {ReadonlyArray<{title: string, value: unknown, unknown?: boolean}>}
 */
export function getAccessLevelOptionsIncluding(raw) {
  const text = cleanText(raw);
  if (text === '' || isKnownAccessLevel(text)) {
    return currentOptions;
  }
  return [...currentOptions, { title: `${text} ${UNKNOWN_OPTION_SUFFIX}`, value: raw, unknown: true }];
}

/**
 * แยก marker `:ACCESS_LEVEL_REQUIRED:` ออกจากข้อความตอบของบอท.
 * - เจอ marker ⇒ `text` = ข้อความที่ตัด marker ทุกตัวออกแล้ว trim, `accessRequired` = true
 * - ไม่เจอ ⇒ `text` = ข้อความเดิมไม่แตะ, `accessRequired` = false
 * - null / undefined ⇒ `{ text: '', accessRequired: false }`
 * @param {unknown} text
 * @returns {{ text: string, accessRequired: boolean }}
 */
export function parseAccessLevelMarker(text) {
  const originalText = text == null ? '' : String(text);

  if (!originalText.includes(ACCESS_LEVEL_MARKER)) {
    return { text: originalText, accessRequired: false };
  }

  return {
    text: originalText.replace(ACCESS_LEVEL_MARKER_PATTERN_GLOBAL, '').trim(),
    accessRequired: true,
  };
}
